import { Map } from 'immutable';
import { CoreUtilities } from '../../core/utilities/core.utilities';
import { Injectable } from '@angular/core';
import { jsonrepair } from 'jsonrepair';

@Injectable()
export class GptTransformerService {

  public constructor(protected coreUtilities: CoreUtilities) {}

  public convertPlainText(plainText: string, machineReadable: boolean | 'bulletpoints' = false): string | string[] {
    /* Result */
    const textArray = [];
    const bulletPoints = [];
    try {
      /* Normalize newlines and preserve line structure */
      const lines = plainText
        .replace(/(\r\n|\r)/g, '\n') // Normalize line endings to \n
        .replace(/^[\x00-\x1F\x7F�]+/, '') // Remove symbols at the beginning
        .split('\n')
        .map(line => line.trim()) // Trim each line
        .filter(line => line.length > 0); // Remove empty lines
      let listItems = [];
      let isOrdered = false;
      let inCodeBlock = false;
      let codeBlockLines = [];
      let codeBlockLang = '';
      let inTable = false;
      let tableLines = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line === '') {
          /* Skip empty lines */
          continue;
        }
        if (line.startsWith('```')) {
          if (!inCodeBlock) {
            inCodeBlock = true;
            codeBlockLang = line.replace('```', '').trim() || 'text';
            codeBlockLines = [];
            continue;
          } else {
            inCodeBlock = false;
            textArray.push(`<pre><code class="language-${codeBlockLang}">${this.escapeHtml(codeBlockLines.join('\n'))}</code></pre>`);
            codeBlockLines = [];
            codeBlockLang = '';
            continue;
          }
        }
        if (inCodeBlock) {
          codeBlockLines.push(line);
          continue;
        }
        if (line.startsWith('|') && !inTable) {
          tableLines.push(line);
          inTable = true;
          continue;
        } else if (inTable) {
          if (line.startsWith('|')) {
            tableLines.push(line);
          } else {
            inTable = false;
            if (tableLines.length > 0) {
              const tableHtml = this.processTable(tableLines);
              if (tableHtml) {
                textArray.push(tableHtml);
              }
            }
            tableLines = [];
          }
          continue;
        }
        // Check for numbered list items
        if (/^\d+\.\s+/.test(line)) {
          const match = line.match(/^(\d+)\.\s*(.*)/);
          if (match) {
            const listItemText = match[2];
            bulletPoints.push(this.stripHtml(listItemText));
            if (listItems.length === 0 && textArray.length > 0) {
              let lastItem = textArray.pop();
              if (lastItem.startsWith('<p>') && !lastItem.endsWith('</p>')) {
                lastItem += '</p>';
              }
              textArray.push(lastItem);
            }
            listItems.push(`<li>${this.processInlineMarkdown(listItemText)}</li>`);
            isOrdered = true;
          }
        } else if (/^\s*\*\s+/.test(line)) {
          // Check for bulleted list items
          const match = line.match(/^\s*\*\s*(.*)/);
          if (match) {
            const listItemText = match[1];
            bulletPoints.push(this.stripHtml(listItemText));
            if (listItems.length === 0 && textArray.length > 0) {
              let lastItem = textArray.pop();
              if (lastItem.startsWith('<p>') && !lastItem.endsWith('</p>')) {
                lastItem += '</p>';
              }
              textArray.push(lastItem);
            }
            listItems.push(`<li>${this.processInlineMarkdown(listItemText)}</li>`);
            isOrdered = false;
          }
        } else {
          // Handle non-list lines (e.g., introductory paragraph or headings)
          if (listItems.length > 0) {
            let listText = isOrdered ? '<ol>' : '<ul>';
            listText += listItems.join('');
            listText += isOrdered ? '</ol>' : '</ul>';
            textArray.push(listText);
            listItems = [];
          }
          if (line) {
            if (textArray.length > 0 && textArray[textArray.length - 1].startsWith('<p>')) {
              textArray[textArray.length - 1] += ` ${this.processInlineMarkdown(line)}`;
            } else {
              textArray.push(`<p>${this.processInlineMarkdown(line)}`);
            }
          }
        }
      }
      // Ensure any remaining list items or open paragraph are closed
      if (listItems.length > 0) {
        let listText = isOrdered ? '<ol>' : '<ul>';
        listText += listItems.join('');
        listText += isOrdered ? '</ol>' : '</ul>';
        textArray.push(listText);
      }
      if (textArray.length > 0 && textArray[textArray.length - 1].startsWith('<p>') && !textArray[textArray.length - 1].endsWith('</p>')) {
        textArray[textArray.length - 1] += '</p>';
      }
      if (inCodeBlock && codeBlockLines.length > 0) {
        textArray.push(`<pre><code class="language-${codeBlockLang}">${this.escapeHtml(codeBlockLines.join('\n'))}</code></pre>`);
      }
      if (inTable && tableLines.length > 0) {
        const tableHtml = this.processTable(tableLines);
        if (tableHtml) {
          textArray.push(tableHtml);
        }
      }
    } catch (error) {
      console.error('Error in convertPlainText:', error);
      return `<p>Error processing text: ${this.escapeHtml(plainText)}</p>`; // Fallback to raw text on error
    }
    return machineReadable === 'bulletpoints' ? bulletPoints : textArray.join('');
  }

  protected escapeHtml(text) {
    return text;
    // const map = { '&': '&', '<': '<', '>': '>', '"': '"' };
    // return text.replace(/[&<>"']/g, m => map[m]);
  }

  protected stripHtml(text) {
    return text.replace(/<[^>]+>/g, '');
  }

  protected processInlineMarkdown(text) {
    try {
      // Handle bold (**text**) with non-greedy match
      text = text.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>'); // **bold**
      // Handle italic (*text*) with paired asterisks
      text = text.replace(/\*([^*]+?)\*/g, '<em>$1</em>'); // *italic*
      text = text.replace(/`([^`]+?)`/g, '<code>$1</code>');
      // Handle links with optional emphasis
      text = text.replace(/\*\*\[([^\]]+?)\]\((https?:\/\/[^\s)]+?)\)\*\*/g, (match, linkText, url) => this.processLink(match, '<strong>', linkText, url, '</strong>'));
      text = text.replace(/\*\[([^\]]+?)\]\((https?:\/\/[^\s)]+?)\)\*/g, (match, linkText, url) => this.processLink(match, '<em>', linkText, url, '</em>'));
      text = text.replace(/\[`([^\]]+?)`\]\((https?:\/\/[^\s)]+?)\)/g, (match, linkText, url) => this.processLink(match, '<code>', linkText, url, '</code>'));
      text = text.replace(/\[([^\]]+?)\]\((https?:\/\/[^\s)]+?)\)/g, (match, linkText, url) => this.processLink(match, '', linkText, url, ''));
      // Handle standalone URLs and emails
      text = text.replace(/(https?:\/\/[^\s<]+?)(?![^<]*>|[^[]*\])/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
      text = text.replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(?![^<]*>|[^[]*\])/g, '<a href="mailto:$1">$1</a>');
    } catch (error) {
      console.error('Error in processInlineMarkdown:', error);
      return text; // Return unprocessed text if error occurs
    }
    return text;
  }

  protected processLink(match, prefix, linkText, url, suffix) {
    const processedText = this.processInlineMarkdown(linkText);
    const baseLink = `<a href="${url}" target="_blank" rel="noopener noreferrer">${processedText}</a>`;
    return `${prefix}${baseLink}${suffix}`;
  }

  protected processTable(lines) {
    if (lines.length < 2 || !lines.some(line => line.includes('|'))) {
      return '';
    } // Require at least one |-delimited line
    let tableHtml = '<table>';
    let headerIndex = 0;
    // Find the first row with | as the header
    while (headerIndex < lines.length && !lines[headerIndex].trim().startsWith('|')) {
      headerIndex++;
    }
    if (headerIndex >= lines.length) {
      return '';
    } // No valid table found
    const headers = lines[headerIndex].trim().split('|').filter(cell => cell.trim() !== '');
    tableHtml += '<thead><tr>';
    headers.forEach(header => { tableHtml += `<th>${this.processInlineMarkdown(this.escapeHtml(header.trim()))}</th>`; });
    tableHtml += '</tr></thead>';
    // Find separator row
    let separatorIndex = -1;
    for (let i = headerIndex + 1; i < lines.length; i++) {
      if (lines[i].trim().match(/^\|[\s:-]+(\|[\s:-]+)*\|$/)) {
        separatorIndex = i;
        break;
      }
    }
    tableHtml += '<tbody>';
    for (let i = (separatorIndex > -1 ? separatorIndex + 1 : 0); i < lines.length; i++) {
      if (lines[i].trim().startsWith('|')) {
        const cells = lines[i].trim().split('|').filter(cell => cell.trim() !== '');
        if (cells.length > 0) {
          tableHtml += '<tr>';
          cells.forEach(cell => { tableHtml += `<td>${this.processInlineMarkdown(this.escapeHtml(cell.trim()))}</td>`; });
          tableHtml += '</tr>';
        }
      }
    }
    tableHtml += '</tbody>';
    tableHtml += '</table>';
    return tableHtml;
  }

  public parse(plainText: string) {
    /* Remove all tags */
    plainText = this.coreUtilities.unescapeHtml(plainText);
    /* Check if it's already a valid JSON Object */
    let plainJson;
    try {
      plainJson = JSON.parse(plainText);
    } catch (e) {}
    if (plainJson !== undefined && plainJson !== null) {
      return Map<string, { type: string, blank: string, parsed: any, html: string }>().set('json', { type: 'json', blank: '', parsed: plainJson, html: '' });
    }

    /* The regular expression */
    const regExp = new RegExp(/```(\w*)([\s\S]*)```/gm);

    let result = Map<string, { type: string, blank: string, parsed: any, html: string }>();

    /* Find the matches */
    let matches: RegExpExecArray;
    while ((matches = regExp.exec(plainText)) !== null) {
      if (matches.index === regExp.lastIndex) {
        regExp.lastIndex++;
      }
      let key: string;
      const parsed = { type: '', blank: '', parsed: undefined, html: '' };
      matches.forEach((match, groupIndex) => {
        switch (groupIndex) {
          case 0:
            key = 'response_json';
            break;
          case 1:
            parsed.type = match;
            break;
          case 2:
            parsed.blank = match;
            try {
              try {
                // Use jsonrepair to fix the malformed JSON
                parsed.parsed = JSON.parse(jsonrepair(match));
              } catch (error) {
              }
            } catch (e) {
            }
            break;
        }
      });
      result = result.set(key, parsed);
    }

    /* Return result */
    return result;
  }

}
